import { toast } from 'sonner';
import { create, type StoreApi } from 'zustand';
import { MessageStatus } from '@avanta/shared/constants/chat';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { type OptimisticMessage, type ReadReceipts } from '@avanta/shared/types/chat';
import { toPending, toSent, uuid, withOverrides } from '@avanta/shared/utils/chat';
import { useChatStore } from '.';

interface MessagesStore {
  messages: Map<string, OptimisticMessage[]>;
  pinnedMessages: Map<string, OptimisticMessage | null>;
  readReceipts: Map<string, ReadReceipts>;
  isLoading: Map<string, boolean>;

  loadMessages: (channelId: string) => Promise<void>;
  sendTextMessage: (channelId: string, text: string) => Promise<void>;
  retryMessage: (channelId: string, tempId: string) => Promise<void>;
  sendFileMessage: (
    channelId: string,
    draft: { files: File[]; send: () => Promise<void> }
  ) => Promise<void>;
  editMessage: (message: OptimisticMessage, newText: string) => Promise<void>;
  deleteMessage: (message: OptimisticMessage) => Promise<void>;
  pinMessage: (message: OptimisticMessage) => Promise<void>;
  unpinMessage: (channelId: string) => Promise<void>;
  toggleReaction: (message: OptimisticMessage, emoji: string) => Promise<void>;
  upsertMessage: (channelId: string, message: OptimisticMessage) => void;
  confirmMessage: (channelId: string, tempId: string, real: OptimisticMessage) => void;
  setPinnedMessage: (channelId: string, message: OptimisticMessage | null) => void;
  setReadReceipts: (channelId: string, receipts: ReadReceipts) => void;
}

type SetState = StoreApi<MessagesStore>['setState'];
type GetState = StoreApi<MessagesStore>['getState'];

const loadMessages =
  (set: SetState, get: GetState) =>
  async (channelId: string): Promise<void> => {
    const { chat } = useChatStore.getState();
    if (!chat) return;

    set(state => ({
      isLoading: new Map(state.isLoading).set(channelId, true),
    }));

    try {
      const channel = await chat.getChannel(channelId);
      if (!channel) return;

      const history = await channel.getHistory();
      const messages = history.messages.map(message => toSent(message));
      const deliveredTempIds = new Set(
        history.messages
          .map(message => message.meta?.tempId as string | undefined)
          .filter((tempId): tempId is string => Boolean(tempId))
      );
      const pendingMessages = (get().messages.get(channelId) ?? []).filter(
        message => message.tempId && !deliveredTempIds.has(message.tempId)
      );

      const pinnedMessage = await channel.getPinnedMessage();
      const pinned = pinnedMessage ? toSent(pinnedMessage) : null;

      set(state => ({
        messages: new Map(state.messages).set(channelId, [...messages, ...pendingMessages]),
        pinnedMessages: new Map(state.pinnedMessages).set(channelId, pinned),
        isLoading: new Map(state.isLoading).set(channelId, false),
      }));
    } catch {
      set(state => ({
        isLoading: new Map(state.isLoading).set(channelId, false),
      }));
    }
  };

const sendTextMessage =
  (set: SetState, get: GetState) =>
  async (channelId: string, text: string): Promise<void> => {
    const { chat } = useChatStore.getState();
    if (!chat) return;

    const tempId = uuid();
    const currentMessages = get().messages.get(channelId) ?? [];
    const optimistic = toPending(text, chat.currentUser.id, channelId, tempId);

    set(state => ({
      messages: new Map(state.messages).set(channelId, [...currentMessages, optimistic]),
    }));

    try {
      const channel = await chat.getChannel(channelId);
      if (!channel) throw new Error(t('Chat.Messages.channel.notFound', { ns: LOCIZE_NS.CHAT }));

      const result = await channel.sendText(text, { meta: { tempId } });
      const timetoken = (result as { timetoken?: string } | undefined)?.timetoken;
      set(state => ({
        messages: new Map(state.messages).set(
          channelId,
          (state.messages.get(channelId) ?? []).map(msg =>
            msg.tempId === tempId
              ? withOverrides(msg, {
                  status: MessageStatus.sent,
                  ...(timetoken ? { timetoken: String(timetoken) } : {}),
                })
              : msg
          )
        ),
      }));
    } catch {
      set(state => ({
        messages: new Map(state.messages).set(
          channelId,
          (state.messages.get(channelId) ?? []).map(msg =>
            msg.tempId === tempId ? withOverrides(msg, { status: MessageStatus.failed }) : msg
          )
        ),
      }));
      toast.error(t('Chat.Messages.send.error', { ns: LOCIZE_NS.CHAT }));
    }
  };

const retryMessage =
  (set: SetState, get: GetState) =>
  async (channelId: string, tempId: string): Promise<void> => {
    const allMessages = get().messages.get(channelId) ?? [];
    const failed = allMessages.find(msg => msg.tempId === tempId);
    if (!failed) return;

    const { chat } = useChatStore.getState();
    if (!chat) return;

    set(state => ({
      messages: new Map(state.messages).set(
        channelId,
        (state.messages.get(channelId) ?? []).map(msg =>
          msg.tempId === tempId ? withOverrides(msg, { status: MessageStatus.sending }) : msg
        )
      ),
    }));

    try {
      const channel = await chat.getChannel(channelId);
      if (!channel) throw new Error(t('Chat.Messages.channel.notFound', { ns: LOCIZE_NS.CHAT }));

      const result = await channel.sendText(failed.text, { meta: { tempId } });
      const timetoken = (result as { timetoken?: string } | undefined)?.timetoken;
      set(state => ({
        messages: new Map(state.messages).set(
          channelId,
          (state.messages.get(channelId) ?? []).map(msg =>
            msg.tempId === tempId
              ? withOverrides(msg, {
                  status: MessageStatus.sent,
                  ...(timetoken ? { timetoken: String(timetoken) } : {}),
                })
              : msg
          )
        ),
      }));
    } catch {
      set(state => ({
        messages: new Map(state.messages).set(
          channelId,
          (state.messages.get(channelId) ?? []).map(msg =>
            msg.tempId === tempId ? withOverrides(msg, { status: MessageStatus.failed }) : msg
          )
        ),
      }));
      toast.error(t('Chat.Messages.retry.error', { ns: LOCIZE_NS.CHAT }));
    }
  };

const sendFileMessage =
  () =>
  async (
    _channelId: string,
    draft: { files: File[]; send: () => Promise<void> }
  ): Promise<void> => {
    await draft.send();
  };

const editMessage =
  (set: SetState, get: GetState) =>
  async (message: OptimisticMessage, newText: string): Promise<void> => {
    const { channelId } = message;
    const snapshot = get().messages.get(channelId) ?? [];

    set(state => ({
      messages: new Map(state.messages).set(
        channelId,
        (state.messages.get(channelId) ?? []).map(msg =>
          msg.timetoken === message.timetoken
            ? withOverrides(msg, {
                text: newText,
                actions: {
                  ...msg.actions,
                  edited: [{ uuid: '', actionTimetoken: String(Date.now()) }],
                },
              })
            : msg
        )
      ),
    }));

    try {
      const updated = await message.editText(newText);
      get().upsertMessage(channelId, toSent(updated));
    } catch {
      set(state => ({
        messages: new Map(state.messages).set(channelId, snapshot),
      }));
      toast.error(t('Chat.Messages.edit.error', { ns: LOCIZE_NS.CHAT }));
    }
  };

const deleteMessage =
  (set: SetState, get: GetState) =>
  async (message: OptimisticMessage): Promise<void> => {
    const { channelId } = message;
    const snapshot = get().messages.get(channelId) ?? [];

    set(state => ({
      messages: new Map(state.messages).set(
        channelId,
        (state.messages.get(channelId) ?? []).map(msg =>
          msg.timetoken === message.timetoken ? withOverrides(msg, { deleted: true }) : msg
        )
      ),
    }));

    try {
      const edited = await message.editText(t('Chat.Messages.deleted', { ns: LOCIZE_NS.CHAT }));
      await edited.delete({ soft: true });
    } catch {
      set(state => ({
        messages: new Map(state.messages).set(channelId, snapshot),
      }));
      toast.error(t('Chat.Messages.delete.error', { ns: LOCIZE_NS.CHAT }));
    }
  };

const pinMessage =
  (set: SetState, get: GetState) =>
  async (message: OptimisticMessage): Promise<void> => {
    const { channelId } = message;
    const snapshotPin = get().pinnedMessages.get(channelId) ?? null;

    set(state => ({
      pinnedMessages: new Map(state.pinnedMessages).set(channelId, message),
    }));

    try {
      await message.pin();
    } catch {
      set(state => ({
        pinnedMessages: new Map(state.pinnedMessages).set(channelId, snapshotPin),
      }));
      toast.error(t('Chat.Messages.pin.error', { ns: LOCIZE_NS.CHAT }));
    }
  };

const unpinMessage =
  (set: SetState, get: GetState) =>
  async (channelId: string): Promise<void> => {
    const { chat } = useChatStore.getState();
    if (!chat) return;

    const snapshotPin = get().pinnedMessages.get(channelId) ?? null;

    set(state => ({
      pinnedMessages: new Map(state.pinnedMessages).set(channelId, null),
    }));

    try {
      const channel = await chat.getChannel(channelId);
      if (!channel) throw new Error('Channel not found');
      await channel.unpinMessage();
    } catch {
      set(state => ({
        pinnedMessages: new Map(state.pinnedMessages).set(channelId, snapshotPin),
      }));
      toast.error(t('Chat.Messages.unpin.error', { ns: LOCIZE_NS.CHAT }));
    }
  };

const toggleReaction =
  (set: SetState, get: GetState) =>
  async (message: OptimisticMessage, emoji: string): Promise<void> => {
    const { channelId } = message;
    const snapshot = get().messages.get(channelId) ?? [];
    const { chat } = useChatStore.getState();
    if (!chat) return;

    const currentUserId = chat.currentUser.id;
    const currentReactions = message.reactions ?? {};
    const reactionList = currentReactions[emoji] ?? [];
    const alreadyReacted = reactionList.some(reaction => reaction.uuid === currentUserId);

    const updatedReactions = alreadyReacted
      ? {
          ...currentReactions,
          [emoji]: reactionList.filter(reaction => reaction.uuid !== currentUserId),
        }
      : {
          ...currentReactions,
          [emoji]: [...reactionList, { uuid: currentUserId, actionTimetoken: Date.now() }],
        };

    set(state => ({
      messages: new Map(state.messages).set(
        channelId,
        (state.messages.get(channelId) ?? []).map(msg =>
          msg.timetoken === message.timetoken
            ? withOverrides(msg, { reactions: updatedReactions })
            : msg
        )
      ),
    }));

    try {
      const updated = await message.toggleReaction(emoji);
      get().upsertMessage(channelId, toSent(updated));
    } catch {
      set(state => ({
        messages: new Map(state.messages).set(channelId, snapshot),
      }));
      toast.error(t('Chat.Messages.reaction.error', { ns: LOCIZE_NS.CHAT }));
    }
  };

const upsertMessage =
  (set: SetState) =>
  (channelId: string, message: OptimisticMessage): void =>
    set(state => {
      const current = state.messages.get(channelId) ?? [];

      let idx = current.findIndex(msg => msg.timetoken === message.timetoken);

      if (idx === -1) {
        const incomingTempId = message.tempId ?? (message.meta?.tempId as string | undefined);
        if (incomingTempId) {
          idx = current.findIndex(msg => msg.tempId === incomingTempId);
        }
      }

      if (idx >= 0) {
        message.clientId = current[idx].clientId;
      }

      const next =
        idx >= 0
          ? current.map((msg, position) => (position === idx ? message : msg))
          : [...current, message];
      return { messages: new Map(state.messages).set(channelId, next) };
    });

const confirmMessage =
  (set: SetState) =>
  (channelId: string, tempId: string, real: OptimisticMessage): void =>
    set(state => {
      const current = state.messages.get(channelId) ?? [];
      const next = current.map(msg => {
        if (msg.tempId !== tempId) return msg;
        real.clientId = msg.clientId;
        return real;
      });
      return { messages: new Map(state.messages).set(channelId, next) };
    });

const setPinnedMessage =
  (set: SetState) =>
  (channelId: string, message: OptimisticMessage | null): void =>
    set(state => ({
      pinnedMessages: new Map(state.pinnedMessages).set(channelId, message),
    }));

const setReadReceipts =
  (set: SetState) =>
  (channelId: string, receipts: ReadReceipts): void =>
    set(state => ({
      readReceipts: new Map(state.readReceipts).set(channelId, receipts),
    }));

export const useMessagesStore = create<MessagesStore>()((set, get) => ({
  messages: new Map(),
  pinnedMessages: new Map(),
  readReceipts: new Map(),
  isLoading: new Map(),

  loadMessages: loadMessages(set, get),
  sendTextMessage: sendTextMessage(set, get),
  retryMessage: retryMessage(set, get),
  sendFileMessage: sendFileMessage(),
  editMessage: editMessage(set, get),
  deleteMessage: deleteMessage(set, get),
  pinMessage: pinMessage(set, get),
  unpinMessage: unpinMessage(set, get),
  toggleReaction: toggleReaction(set, get),
  upsertMessage: upsertMessage(set),
  confirmMessage: confirmMessage(set),
  setPinnedMessage: setPinnedMessage(set),
  setReadReceipts: setReadReceipts(set),
}));
